import React, { useEffect, useState } from 'react';
import { auditApi } from '../lib/api';
import { FileText, ShieldAlert, ShieldCheck } from 'lucide-react';

// Widget "Tentativi di accesso negato": login rifiutati (utenti non invitati)
// negli ultimi 30 giorni, da audit action `login_denied`.
function DeniedAccessWidget() {
  const [denied, setDenied] = useState(null);

  useEffect(() => {
    auditApi.list({ action: 'login_denied', limit: 50 })
      .then(r => setDenied(r.data))
      .catch(() => setDenied([]));
  }, []);

  if (denied === null) return null;
  const cutoff = Date.now() - 30 * 24 * 60 * 60 * 1000;
  const recent = denied.filter(l => new Date(l.created_at).getTime() >= cutoff);
  const alarmed = recent.length > 0;

  return (
    <div className={`border p-4 mb-6 ${alarmed ? 'bg-red-50 border-red-300' : 'bg-emerald-50 border-emerald-200'}`} data-testid="denied-access-widget">
      <div className="flex items-center gap-2">
        {alarmed
          ? <ShieldAlert className="w-5 h-5 text-red-600" />
          : <ShieldCheck className="w-5 h-5 text-emerald-600" />}
        <p className="text-sm font-semibold text-slate-900">
          Tentativi di accesso negati (30 giorni):{' '}
          <span className={alarmed ? 'text-red-700' : 'text-emerald-700'} data-testid="denied-access-count">{recent.length}</span>
        </p>
      </div>
      {alarmed ? (
        <div className="mt-3 space-y-1">
          {recent.slice(0, 5).map(l => (
            <div key={l.log_id} className="flex items-center gap-3 text-xs text-slate-600" data-testid={`denied-row-${l.log_id}`}>
              <span className="text-slate-400 w-36 shrink-0">{new Date(l.created_at).toLocaleString('it-IT')}</span>
              <span className="font-mono truncate">{l.user_email}</span>
            </div>
          ))}
          {recent.length > 5 && (
            <p className="text-[11px] text-slate-400">…e altri {recent.length - 5} tentativi</p>
          )}
        </div>
      ) : (
        <p className="text-xs text-slate-500 mt-1">Nessun login rifiutato di recente — accessi solo da utenti invitati.</p>
      )}
    </div>
  );
}

export default function AuditLogPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    auditApi.list({ limit: 100 })
      .then(r => setLogs(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-8"><div className="w-8 h-8 border-2 border-black border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="p-4 md:p-8 max-w-[1920px]" data-testid="audit-log-page">
      <div className="mb-8">
        <h1 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-slate-900">Registro Audit</h1>
        <p className="text-slate-500 mt-1">Tracciamento di tutte le operazioni effettuate</p>
      </div>

      <DeniedAccessWidget />

      <div className="bg-white border border-slate-200">
        {logs.length === 0 ? (
          <div className="p-12 text-center text-slate-400">
            <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>Nessun log di audit</p>
          </div>
        ) : (
          <table className="w-full data-table">
            <thead>
              <tr>
                <th>Data</th>
                <th>Azione</th>
                <th>Entit&agrave;</th>
                <th>ID</th>
                <th>Utente</th>
                <th>Dettagli</th>
              </tr>
            </thead>
            <tbody>
              {logs.map(log => (
                <tr key={log.log_id} data-testid={`audit-row-${log.log_id}`}>
                  <td data-label="Data" className="text-xs text-slate-500 whitespace-nowrap">
                    {new Date(log.created_at).toLocaleString('it-IT')}
                  </td>
                  <td data-label="Azione">
                    <span className="text-xs uppercase tracking-wider font-medium px-2 py-0.5 border bg-slate-50">
                      {log.action}
                    </span>
                  </td>
                  <td data-label="Entità" className="text-sm">{log.entity_type}</td>
                  <td data-label="ID" className="font-mono text-xs text-slate-500">{log.entity_id}</td>
                  <td data-label="Utente" className="text-sm text-slate-600">{log.user_email}</td>
                  <td data-label="Dettagli" className="text-xs text-slate-400 max-w-[200px] truncate">
                    {JSON.stringify(log.details)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
